import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useMemo, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { config as configCore, utils as utilsCore } from '@industriousapps/excelkits-core';
import logger from '@/utils/logger';
import useGooglePicker from '@/hooks/useGooglePicker';
import { useToast } from '@/hooks/useToast';
import { useAuth, ERROR_CODES as authErrors } from '@/hooks/useAuth';
import GooglePickerButton from '@/components/FormElements/GooglePickerButton';
import ButtonAuthWithGoogle from '@/components/FormElements/ButtonAuthWithGoogle';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PREFIX = 'common: components: Editor: modals: Data: AddNew: GooglePicker:';
const SUPPORTED_FILE_TYPES = configCore.spreadsheets.supportedFileTypes;
const {
  createWrappedError
} = utilsCore;

const GooglePicker = _ref => {
  var _Object$entries2;

  let {
    onChange,
    getGoogleDriveFile,
    isSmallContainer
  } = _ref;
  const {
    user,
    getGoogleAccessToken,
    signInWithGoogle
  } = useAuth();
  const {
    addToast: sendNotification
  } = useToast();
  const {
    openPicker
  } = useGooglePicker();
  const {
    0: isSignInLoading,
    1: setIsSignInLoading
  } = useState(false);
  const {
    0: isPickLoading,
    1: setIsPickLoading
  } = useState(false);
  const {
    0: requestSignUp,
    1: setRequestSignUp
  } = useState(false);
  const hasGoogleAuthSession = useMemo(() => {
    var _Object$entries;

    return Boolean((user === null || user === void 0 ? void 0 : user.google) && ((_Object$entries = Object.entries(user === null || user === void 0 ? void 0 : user.google)) === null || _Object$entries === void 0 ? void 0 : _Object$entries.length));
  }, [user]);
  const handleFile = useCallback(fileMimeType => {
    if (!fileMimeType) return;
    let ext = '';

    switch (fileMimeType) {
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        ext = 'xlsx';
        break;

      case 'application/vnd.google-apps.spreadsheet':
        ext = 'xlsx';
        break;

      case 'application/vnd.ms-excel.sheet.binary.macroEnabled.12':
        ext = 'xlsb';
        break;

      case 'application/vnd.ms-excel.sheet.macroEnabled.12':
        ext = 'xlsm';
        break;

      case 'application/vnd.ms-excel':
        ext = 'xls';
        break;

      default:
        ext = 'unknown';
        break;
    } // check whether file is supported or not


    if (SUPPORTED_FILE_TYPES.includes(ext)) {
      // supported
      return true;
    } else {
      // unsupported
      sendNotification({
        type: 'error',
        description: 'Unsupported file'
      });
      return false;
    }
  }, [sendNotification]);
  const handleOnPickFile = useCallback(async fileData => {
    try {
      const blob = await getGoogleDriveFile(fileData.fileId, fileData.fileName, fileData.fileMimeType, fileData.accessToken); // Convert Blob to File

      const file = new File([blob], fileData.fileName, {
        type: fileData.fileMimeType
      });
      if (file) onChange(file, {
        origin: 'google',
        originFileId: fileData.fileId,
        originFileType: fileData.fileMimeType,
        originUserId: fileData.fileUserId
      });
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} handleOnPickFile: unexpected error getting google drive file`, err);
      logger.error(wrappedError);
      Sentry.captureException(wrappedError);
      sendNotification({
        type: 'error',
        description: err.userMessage || 'Unexpected error during loading file'
      });
    }
  }, [getGoogleDriveFile, onChange, sendNotification]);
  const handleOpenPicker = useCallback(async google => {
    setIsPickLoading(true);
    setRequestSignUp(false); // Sanity check

    try {
      const file = await openPicker(google);

      if (file) {
        const isValid = handleFile(file.fileMimeType);
        if (isValid) handleOnPickFile(file);
      }
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} handleOpenPicker: unexpected open picker error`, err);
      logger.error(wrappedError);
      Sentry.captureException(wrappedError);
      sendNotification({
        type: 'error',
        description: err.userMessage || 'Unexpected error, please try again'
      });
    } finally {
      setIsPickLoading(false);
    }
  }, [handleFile, handleOnPickFile, openPicker, sendNotification]);

  const handleGoogleSignIn = async () => {
    setIsSignInLoading(true);
    setRequestSignUp(false);
    let localUser = user; // Attempt to login with Google Auth

    if (!localUser) {
      try {
        localUser = await signInWithGoogle();
      } catch (err) {
        setIsSignInLoading(false);

        if (err.code === authErrors.userNotSignedUp) {
          setRequestSignUp(true);
          sendNotification({
            type: 'error',
            description: 'You must first sign up for a free account to import files from Google Drive'
          });
          return;
        }

        const wrappedError = createWrappedError(`${PREFIX} handleGoogleSignIn: unable to sign in user`, err);
        logger.error(wrappedError);
        Sentry.captureException(wrappedError);
        sendNotification({
          type: 'error',
          description: err.userMessage || 'Unexpected error, please try again'
        });
        return;
      }
    }

    let accessToken;

    try {
      accessToken = await getGoogleAccessToken(localUser.uid);
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} handleGoogleSignIn: failed to get google access token`, err);
      logger.error(wrappedError);
      Sentry.captureException(wrappedError);
      sendNotification({
        type: 'error',
        description: 'Unexpected error during login'
      });
    } // @ts-ignore


    if (accessToken) {
      sendNotification({
        type: 'success',
        description: 'Successfully connected'
      });
    }

    setIsSignInLoading(false);
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!hasGoogleAuthSession && /*#__PURE__*/_jsx(ButtonAuthWithGoogle, {
      variant: "signin",
      helperText: requestSignUp ? '' : 'Connect files from Google Drive',
      requestSignUp: requestSignUp,
      loading: isSignInLoading,
      onClick: handleGoogleSignIn
    }), hasGoogleAuthSession && ((_Object$entries2 = Object.entries((user === null || user === void 0 ? void 0 : user.google) || {})) === null || _Object$entries2 === void 0 ? void 0 : _Object$entries2.map(_ref2 => {
      let [googleUserId, userData] = _ref2;
      return /*#__PURE__*/_jsx(GooglePickerButton, _objectSpread({
        onClick: () => handleOpenPicker(_objectSpread(_objectSpread({}, userData), {}, {
          googleUserId
        })),
        loading: isPickLoading
      }, isSmallContainer ? {
        size: 'small'
      } : {}), googleUserId);
    }))]
  });
};

export default GooglePicker;